import {
  FlowTrackerSteps,
  PARTIAL_TRANSFER_FAQ,
  validationErrorMessage,
} from 'common/constants';
import Accordion from 'components/common/Accordion/Accordion';
import {
  ATBeaconButton,
  ATBeaconNotification,
} from 'components/common/beacon/beaconRegistration';
import FlowTracker from 'components/common/FlowTracker/FlowTracker';
import Plus from 'components/common/icons/Plus';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/state/store';
import {
  HoldingTransferType,
  partialTransferLabel,
  partialTransferMessage,
} from 'utils/constants';
import { CTAVariants } from '@trp-beacon-ui-kit/foundation/dist/_common/types/cta-variant';
import { ManualHolding } from '../../../../api/atv/atvApi.types';
import { setManualSelectedHoldings } from '../../transferSlice';
import '../partialTransfer/PartialTransfer.scss';
import PartialTransferHolding from '../partialTransfer/partialTransferHolding/PartialTransferHolding';

interface ManualPartialTransferProps {
  showValidation: boolean;
  setShowValidation: (isEnabled: boolean) => void;
  setIsStepValid: (isEnabled: boolean) => void;
}

const ManualPartialTransfer: React.FC<ManualPartialTransferProps> = (props) => {
  const { showValidation, setShowValidation, setIsStepValid } = props;
  const [notificationMessage, setNotificationMessage] = useState(
    validationErrorMessage.MANUAL_PARTIAL_TRANSFER,
  );
  const dispatch = useDispatch();

  const manualSelectedHoldings = useSelector(
    (state: RootState) => state.transfer.manualSelectedHoldings,
  );

  const [holdings, setHoldings] = useState<ManualHolding[]>(
    manualSelectedHoldings ?? [
      {
        id: 1,
        name: '',
        symbol: '',
        quantity: 0,
        transferType: HoldingTransferType.TOTAL,
        restored: true,
      },
    ],
  );
  const [nextId, setNextId] = useState<number>(
    manualSelectedHoldings ? manualSelectedHoldings.length + 1 : 2,
  );

  useEffect(() => {
    const totalQuantity = holdings
      .filter((holding) => holding.restored === true)
      .reduce((sum, accum) => sum + accum.quantity, 0);
    const emptyHoldings = holdings.every((holding) => holding.restored === false);
    const emptySymbol = holdings.every((holding) => holding.symbol === '');
    if (emptySymbol) {
      setNotificationMessage(validationErrorMessage.MANUAL_PARTIAL_TRANSFER);
      setIsStepValid(false);
    } else if (emptyHoldings) {
      setNotificationMessage(validationErrorMessage.EMPTY_HOLDING);
      setIsStepValid(false);
    } else if (Number(totalQuantity) === 0) {
      setNotificationMessage(validationErrorMessage.ZERO_QUANTITY);
      setIsStepValid(false);
    } else {
      setIsStepValid(true);
    }
    dispatch(setManualSelectedHoldings(holdings));
  }, [holdings, dispatch]);

  const addHolding = () => {
    const newHolding = {
      id: nextId,
      name: '',
      symbol: '',
      quantity: 0,
      transferType: HoldingTransferType.TOTAL,
      restored: true,
    };
    setHoldings([...holdings, newHolding]);
    setNextId(nextId + 1);
    updateSelectedHoldings(newHolding, 'add');
  };

  const getSelectedHolding = (holding: ManualHolding) => {
    return holdings.find((h: ManualHolding) => h.id === holding.id) ?? null;
  };

  const updateSelectedHoldings = (
    holding: ManualHolding,
    action: 'add' | 'remove' | 'update',
    updateVal?: any,
  ) => {
    let updatedHoldings = holdings;

    switch (action) {
      case 'add':
        updatedHoldings = [...updatedHoldings, holding];
        break;
      case 'remove':
        updatedHoldings = updatedHoldings.map((h: ManualHolding) =>
          h.id === holding.id ? { ...h, restored: false } : h,
        );
        break;
      case 'update':
        updatedHoldings = updatedHoldings.map((h: ManualHolding) =>
          h.id === holding.id ? { ...h, ...updateVal } : h,
        );
        break;
    }
    setHoldings(updatedHoldings);
  };

  const handleBeaconDismiss = () => {
    setShowValidation(false);
  };

  return (
    <div className="content-container b-grid">
      <div className="content-left b-grid-item b-xs:12-col b-sm:12-col b-md:8-col b-lg:8-col">
        <div className="b-grid">
          <span className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
            <label className="b-typography-sans-serif--headline--100 b-font-weight-bold">
              {partialTransferLabel}
            </label>
            <p>{partialTransferMessage}</p>
            <form id="manualPartialTransferForm">
              {showValidation && (
                <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col b-padding-block-end--micro--jumbo">
                  <ATBeaconNotification
                    type="inline"
                    variant="warning"
                    dismissible
                    onBeaconEventDismiss={() => handleBeaconDismiss()}
                  >
                    {notificationMessage}
                  </ATBeaconNotification>
                </div>
              )}
              <label className="b-typography-sans-serif--body--200 b-font-weight-bold">
                Add holdings you want to transfer
              </label>
              <div className="holdings-wrapper">
                {holdings.map((holding: ManualHolding, index: number) => (
                  <PartialTransferHolding
                    key={index}
                    holding={holding}
                    updateSelectedHoldings={updateSelectedHoldings}
                    selectedHolding={getSelectedHolding(holding)}
                    isManualEntry={true}
                    setNotificationMessage={(message: string) =>
                      setNotificationMessage(message)
                    }
                  />
                ))}
              </div>
              <ATBeaconButton variant={CTAVariants.Secondary} onClick={addHolding}>
                {<Plus slot="prefix" />} Add Holding
              </ATBeaconButton>
            </form>
          </span>
        </div>
      </div>
      <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:4-col b-lg:4-col">
        <div className="flow-tracker-container">
          <FlowTracker currentFlowStep={2} items={FlowTrackerSteps} />
        </div>
        <div className="faq-container">
          <Accordion items={PARTIAL_TRANSFER_FAQ} />
        </div>
      </div>
    </div>
  );
};

export default ManualPartialTransfer;
